import { useMutation, useQuery } from '@tanstack/react-query';

import { isValidFeature } from '../services';
import { useEntitlementsStore } from '../store/entitlements';

export const useBridgeKeeperMutation = () => {
  const { current: env } = useEntitlementsStore();

  const environmentId = env?.id as string | undefined;

  return useMutation({
    mutationKey: ['mutate_get_bridge_keeper'],
    mutationFn: (featureName: string) => {
      return isValidFeature(featureName, environmentId);
    },
  });
};

type FeatureNameInput = string | string[] | undefined;

export const useBridgeKeeper = (featureNames: FeatureNameInput) => {
  const { current: env } = useEntitlementsStore();

  const environmentId = env?.id as string | undefined;
  const isMultiple = Array.isArray(featureNames);

  const { data, status } = useQuery({
    queryKey: ['get_multiple_bridge_keepers', featureNames, environmentId],
    queryFn: async () => {
      if (!featureNames || !environmentId) return isMultiple ? {} : false;

      if (isMultiple) {
        const results = await Promise.allSettled(
          featureNames.map(name => isValidFeature(name, environmentId)),
        );

        const featureMap = featureNames.reduce((acc, name, idx) => {
          const result = results[idx];
          acc[name] =
            result.status === 'fulfilled' ? result.value === true : false;
          return acc;
        }, {} as Record<string, boolean>);

        return featureMap;
      } else {
        return await isValidFeature(featureNames, environmentId);
      }
    },
    enabled:
      Boolean(environmentId) &&
      (isMultiple ? featureNames.length > 0 : Boolean(featureNames)),
    refetchOnWindowFocus: false,
  });

  const isLoading = status === 'pending';

  if (!isMultiple) {
    return {
      showFeature: status === 'success' && data === true,
      isLoading,
    };
  }

  return {
    showFeatures:
      status === 'success' && data ? (data as Record<string, boolean>) : false,
    isLoading,
  };
};
